import React, { useEffect, useState } from 'react';

// Define the type for filters prop
interface CareerFilters {
  departments?: (string | number)[];
  likes?: string[];
  abilities?: string[];
}

// Define the type for each career item
interface Career {
  id: number;
  name: string;
  about_section: string;
  thumbnail_image: string;
}

// Define the API response type
interface CareerApiResponse {
  success: boolean;
  message?: string;
  data?: {
    careers: Career[];
    totalCount: number;
  };
}

// Define component props type
interface CareerDeepListProps {
  filters: CareerFilters;
}

const CareerDeepList: React.FC<CareerDeepListProps> = ({ filters }) => {
  const [careers, setCareers] = useState<Career[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCareers = async () => {
      setLoading(true);
      setError(null);

      const { departments = [], likes = [], abilities = [] } = filters || {};

      try {
        const response = await fetch(`${process.env.REACT_APP_SFE_API}career/careerDeepDiveList`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            department: departments.join(','),
            likes: likes.join(','),
            naturally_good: abilities.join(','),
          }),
        });

        const result: CareerApiResponse = await response.json();

        if (result.success && result.data) {
          setCareers(result.data.careers);
          setTotalCount(result.data.totalCount);
        } else {
          setError(result.message || 'Failed to fetch careers');
        }
      } catch (err) {
        setError('An error occurred while fetching data');
      } finally {
        setLoading(false);
      }
    };

    // Delay the API call by 1 second
    const delayApiCall = setTimeout(() => {
      fetchCareers();
    }, 1000);

    // Cleanup timeout on unmount
    return () => clearTimeout(delayApiCall);
  }, [filters]);

  if (loading) {
    // Use existing project CSS for a visible loading animation.
    // `.page_loader` and inner `.spinner-border` are defined in revamp_style_bkp.css
    return (
      <div className="page_loader" aria-live="polite" aria-busy="true">
        <div className="spinner-border" role="status" />
        <span className="sr-only">Loading careers...</span>
      </div>
    );
  }

  if (error) {
    return <p style={{ color: 'red' }}>{error}</p>;
  }

  const trimText = (text: string, length: number): string => {
    // Strip HTML tags first
    const stripped = text.replace(/<\/?[^>]+(>|$)/g, '');
    // Then decode HTML entities like &nbsp;
    const decoded = stripped
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    return decoded.length > length ? decoded.slice(0, length).trim() + '…' : decoded;
  };

  const getBasePath = (): string => {
    if (window.location.pathname.includes('/user/career-list')) {
      return '/user';
    } else if (window.location.pathname.includes('/student/career-list')) {
      return '/student';
    }
    return '';
  };

  return (
    // <div className="flex-1 polarcontsec tab-content" id="myTabContent">
      <div className="ActiveListings_present">
        {/* <h3 className="font-18 font-500 my-2">{totalCount} Careers found</h3> */}
        {totalCount > 0 && (
          <h3 className="font-18 font-500 my-2">{totalCount} Careers found</h3>
        )}
        {totalCount === 0 && (
          <h3 className="font-18 font-500 my-2">No Careers found</h3>
        )}
        <div className="row EventsBlk">
          {careers.map((career) => (
            <div className="col-lg-6 col-md-12 album_blk_parent" key={career.id}>
              <div className="album_blk bg-white">
                <div className="album_imgblk position-relative">
                  <img src={career.thumbnail_image} alt={career.name} />
                </div>
                <div className="album_contblk eventsbox">
                  <h3 className="font-18 font-600 mb-2" title={career.name}>
                    {trimText(career.name, 15)}
                  </h3>
                  <p className="font-15 line-height-23">
                    {trimText(career.about_section, 130)}
                    <a
                      href={`${window.location.origin}${getBasePath()}/careers/${career.id}`}
                      className="green-link read-more"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {' '}Read More
                    </a>
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    // </div>
  );
};

export default CareerDeepList;
